import { validateConfig, ConfigValidationError } from './configValidator';
import { agencyConfigs } from '../../configs';
import { getDevAgencyDomain } from '@/utils/devAgency';
/**
 * Ошибка загрузки конфигурации
 */
export class ConfigLoadError extends Error {
    constructor(message, options) {
        super(message);
        this.name = 'ConfigLoadError';
        this.cause = options?.cause;
        this.url = options?.url;
        this.httpStatus = options?.httpStatus;
    }
}
/**
 * Получение переменных окружения с проверкой
 * Webpack DefinePlugin внедряет process.env в браузер
 */
function getEnvironment() {
    // Современный подход: проверяем доступность конкретных переменных
    // DefinePlugin заменяет process.env.VARIABLE на значения во время компиляции
    let nodeEnv;
    let configBaseUrl;
    let agencySlug;
    try {
        // @ts-ignore - DefinePlugin заменит эти значения
        nodeEnv = process.env.NODE_ENV || 'development';
        // @ts-ignore - DefinePlugin заменит эти значения  
        configBaseUrl = process.env.B2C_CONFIG_BASE_URL;
        // @ts-ignore - DefinePlugin заменит эти значения
        agencySlug = process.env.B2C_AGENCY_SLUG;
    }
    catch (error) {
        console.warn('⚠️ [CONFIG] process.env недоступен, используем fallback значения');
        nodeEnv = 'development';
        configBaseUrl = undefined;
        agencySlug = undefined;
    }
    const env = {
        NODE_ENV: nodeEnv,
        B2C_CONFIG_BASE_URL: configBaseUrl,
        B2C_AGENCY_SLUG: agencySlug
    };
    return env;
}
/**
 * Определение slug агентства для production
 * В рамках этой задачи используем переменную окружения B2C_AGENCY_SLUG
 */
function getAgencySlug(env) {
    // Приоритет 1: переменная окружения
    if (env.B2C_AGENCY_SLUG) {
        return env.B2C_AGENCY_SLUG;
    }
    // Приоритет 2: в runtime в браузере определяем по host
    if (typeof window !== 'undefined') {
        const host = window.location.hostname;
        // subdomain → slug
        const parts = host.split('.');
        const subdomain = parts[0];
        //
        if (subdomain && subdomain !== 'www') {
            return subdomain;
        }
        // fallback - default
        return 'default';
    }
    throw new ConfigLoadError('Imposible to define [agencySlug]: there isnt [env] and [window.location]');
}
/**
 * Загрузка конфига в dev-режиме из локального файла
 */
async function loadConfigDev() {
    // Если пользователь выбрал агентство через сервисное меню —
    // возвращаем его конфиг прямо из бандла, без HTTP-запроса
    const selectedDomain = getDevAgencyDomain();
    if (selectedDomain) {
        const bundledConfig = agencyConfigs.find(c => c.agencyDomain === selectedDomain);
        if (bundledConfig) {
            console.log('🔧 [CONFIG] Загрузка dev-конфига из бандла для домена:', selectedDomain, {
                agencyAlias: bundledConfig.agencyAlias,
            });
            return bundledConfig;
        }
    }
    const url = '/config.json';
    try {
        console.log('🔧 [CONFIG] Загрузка конфига в dev-режиме из:', url);
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            // Предотвращаем кэширование для dev-режима
            cache: 'no-cache'
        });
        if (!response.ok) {
            throw new ConfigLoadError(`HTTP ошибка при загрузке конфига: ${response.status} ${response.statusText}`, { url, httpStatus: response.status });
        }
        const rawConfig = await response.json();
        // Валидация конфига
        const validatedConfig = validateConfig(rawConfig);
        console.log('✅ [CONFIG] Конфиг успешно загружен и валидирован:', {
            agencyAlias: validatedConfig.agencyAlias,
            version: validatedConfig.version,
            caption: validatedConfig.application.info.caption
        });
        return validatedConfig;
    }
    catch (error) {
        if (error instanceof ConfigValidationError) {
            console.error('❌ [CONFIG] Ошибка валидации конфига:', error.message);
            throw new ConfigLoadError(`Конфиг не прошел валидацию: ${error.message}`, { cause: error, url });
        }
        if (error instanceof ConfigLoadError) {
            throw error;
        }
        if (error instanceof TypeError && error.message.includes('fetch')) {
            console.error('❌ [CONFIG] Сетевая ошибка при загрузке конфига:', error);
            throw new ConfigLoadError('Не удалось подключиться к серверу для загрузки конфига. Убедитесь, что dev-сервер запущен и файл config.json существует.', { cause: error, url });
        }
        console.error('❌ [CONFIG] Неожиданная ошибка загрузки конфига:', error);
        throw new ConfigLoadError(`Неожиданная ошибка загрузки конфига: ${error}`, { cause: error, url });
    }
}
/**
 * Загрузка конфига в prod-режиме из S3/CloudFront
 */
async function loadConfigProd(env) {
    const baseUrl = env.B2C_CONFIG_BASE_URL;
    if (!baseUrl) {
        throw new ConfigLoadError('Не установлена переменная окружения B2C_CONFIG_BASE_URL для production режима');
    }
    const agencySlug = getAgencySlug(env);
    const url = `${baseUrl}/${agencySlug}/config.json`;
    try {
        console.log('🚀 [CONFIG] Загрузка конфига в prod-режиме из:', url);
        const response = await fetch(url);
        if (!response.ok) {
            if (response.status === 404) {
                throw new ConfigLoadError(`Конфиг для агентства "${agencySlug}" не найден`, { url, httpStatus: response.status });
            }
            throw new ConfigLoadError(`HTTP ошибка при загрузке конфига: ${response.status} ${response.statusText}`, { url, httpStatus: response.status });
        }
        const rawConfig = await response.json();
        // Валидация конфига
        const validatedConfig = validateConfig(rawConfig);
        // Дополнительная проверка: slug в конфиге должен совпадать с ожидаемым
        if (validatedConfig.agencyAlias !== agencySlug) {
            throw new ConfigLoadError(`Несоответствие slug агентства: ожидался "${agencySlug}", получен "${validatedConfig.agencyAlias}"`, { url });
        }
        console.log('✅ [CONFIG] Конфиг успешно загружен и валидирован:', {
            agencyAlias: validatedConfig.agencyAlias,
            version: validatedConfig.version,
            caption: validatedConfig.application.info.caption,
            source: 'production'
        });
        return validatedConfig;
    }
    catch (error) {
        if (error instanceof ConfigValidationError) {
            console.error('❌ [CONFIG] Ошибка валидации конфига:', error.message);
            throw new ConfigLoadError(`Конфиг не прошел валидацию: ${error.message}`, { cause: error, url });
        }
        if (error instanceof ConfigLoadError) {
            throw error;
        }
        if (error instanceof TypeError && error.message.includes('fetch')) {
            console.error('❌ [CONFIG] Сетевая ошибка при загрузке конфига:', error);
            throw new ConfigLoadError('Не удалось подключиться к CDN для загрузки конфига. Проверьте подключение к интернету.', { cause: error, url });
        }
        console.error('❌ [CONFIG] Неожиданная ошибка загрузки конфига:', error);
        throw new ConfigLoadError(`Неожиданная ошибка загрузки конфига: ${error}`, { cause: error, url });
    }
}
/**
 * Основная функция загрузки конфигурации B2C приложения
 * Автоматически определяет режим (dev/prod) и загружает соответствующий конфиг
 */
export async function loadB2cConfig() {
    const env = getEnvironment();
    const isDevelopment = env.NODE_ENV === 'development';
    try {
        if (isDevelopment) {
            return await loadConfigDev();
        }
        else {
            return await loadConfigProd(env);
        }
    }
    catch (error) {
        // Логирование для мониторинга
        console.error('🚨 [CONFIG] Критическая ошибка загрузки конфига:', {
            error: error instanceof Error ? error.message : String(error),
            mode: isDevelopment ? 'development' : 'production',
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            url: window.location.href
        });
        // TODO: Здесь можно добавить отправку в систему мониторинга
        // await sendErrorToMonitoring(error);
        throw error;
    }
}
/**
 * Проверка доступности конфига без его загрузки
 * Полезно для health checks
 */
export async function checkConfigAvailability() {
    try {
        await loadB2cConfig();
        return true;
    }
    catch {
        return false;
    }
}
/**
 * Получение информации о текущей конфигурации окружения
 * Полезно для отладки
 */
export function getConfigEnvironmentInfo() {
    const env = getEnvironment();
    const isDevelopment = env.NODE_ENV === 'development';
    if (isDevelopment) {
        return { mode: 'development' };
    }
    return {
        mode: 'production',
        baseUrl: env.B2C_CONFIG_BASE_URL,
        agencySlug: env.B2C_AGENCY_SLUG
    };
}
